import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import {
  Column,
  EllipsisWithTooltip,
  LayoutCard,
  NoData,
  Table,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import {
  POD_APPLIED_STATUS_RANK,
  getPodAppliedStatus,
  getPodRecommendedRequests,
  isWorkloadOptimizationDisabled,
} from '@cast/split-me/features/cluster/workload-optimization';
import {
  OptimizationWorkloadPod,
  OptimizationWorkloadPodContainer,
} from '@cast/types';
import { PreparedWorkload } from '@cast/split-me/features/workload-optimization';

import { PodListStatusCell } from './PodListStatusCell';

dayjs.extend(advancedFormat);

const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss [.] z';

const RecommendationCell = ({
  containers,
}: {
  containers: OptimizationWorkloadPodContainer[];
}) => {
  const recommended = getPodRecommendedRequests(containers);

  if (!recommended) {
    return (
      <Box
        display="flex"
        alignItems="center"
        gap={12}
        data-testid="pod-list-recommendation-empty"
      >
        <Typography variant="P12R" sx={{ color: 'grey.400' }}>
          —
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={12}
      data-testid="pod-list-recommendation"
    >
      <Stack>
        <Box display="flex" gap={4}>
          <Typography variant="P12R">
            {recommended.cpuCores.toFixed(3)}
          </Typography>
          <Typography variant="P12R" sx={{ color: 'grey.400' }}>
            CPU
          </Typography>
        </Box>
        <Box display="flex" gap={4}>
          <Typography variant="P12R">
            {recommended.memoryGib.toFixed(3)}
          </Typography>
          <Typography variant="P12R" sx={{ color: 'grey.400' }}>
            GiB
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

type Props = {
  pods: OptimizationWorkloadPod[];
  // `PreparedWorkload` is the type Inventory uses everywhere else (via
  // `WorkloadDetailsContext`) and is accepted by
  // `isWorkloadOptimizationDisabled` via its union parameter type.
  workload?: PreparedWorkload;
};

export const PodListTable = ({ pods, workload }: Props) => {
  const isOptimizationDisabled = isWorkloadOptimizationDisabled(workload);

  return (
    <LayoutCard
      title={`${pods.length} pods`}
      headerDivider
      padding={false}
      testId="pod-list-table-card"
      slotProps={{
        title: {
          variant: 'A2',
          color: tokens.theme.fg.structural.secondary,
        },
        header: {
          py: 12,
        },
      }}
    >
      <Table<OptimizationWorkloadPod>
        testId="pod-list-table"
        data={pods}
        rowKey="name"
        emptyPlaceholder={<NoData entity="pods" />}
      >
        <Column<OptimizationWorkloadPod>
          id="name"
          header="pod"
          sortable
          minWidth={150}
          accessor={({ name }) => name}
          renderCell={({ name }) => (
            <EllipsisWithTooltip TypographyProps={{ variant: 'P12M' }}>
              {name}
            </EllipsisWithTooltip>
          )}
        />

        <Column<OptimizationWorkloadPod>
          id="recommendation"
          header="autoscaler recommendation"
          sortable
          width={185}
          sortingType="number"
          accessor={({ containers }) =>
            getPodRecommendedRequests(containers)?.cpuCores ?? 0
          }
          renderCell={({ containers }) => (
            <RecommendationCell containers={containers} />
          )}
        />

        <Column<OptimizationWorkloadPod>
          id="status"
          header="autoscaling status"
          width={140}
          sortable
          sortingType="number"
          accessor={({ isLatestRecommendation, recommendation }) =>
            POD_APPLIED_STATUS_RANK[
              getPodAppliedStatus(
                { isLatestRecommendation, recommendation },
                isOptimizationDisabled
              )
            ]
          }
          renderCell={(pod) => (
            <PodListStatusCell
              pod={pod}
              isOptimizationDisabled={isOptimizationDisabled}
            />
          )}
        />

        <Column<OptimizationWorkloadPod>
          id="appliedAt"
          header="timestamp"
          width={240}
          sortable
          accessor={({ recommendation }) => recommendation?.appliedAt}
          renderCell={({ recommendation }) => {
            const appliedAt = recommendation?.appliedAt;
            return (
              <Typography
                variant="P12R"
                color={appliedAt ? undefined : 'textSecondary'}
              >
                {appliedAt ? dayjs(appliedAt).format(TIMESTAMP_FORMAT) : '—'}
              </Typography>
            );
          }}
        />
      </Table>
    </LayoutCard>
  );
};
